import React, { useContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { RootStoreContext } from '../core/root-store';
import { signOut } from '../user/user-update.service';
import { updateUserProfileInDatabase } from '../user/user.service';
import { auth } from '../firebase/config';
import PTestStore from './p-test.store';
import { PTestCollection } from './onboarding.service';
import { UserRole } from '../user/user.type';
import { OnboardingStep } from './onboarding.type';
import PTestStepsUi from './ui/p-test-steps.ui';
import { trackPersonalityTestCompleted } from '../analytics/analytics.service';

const PTestStepsContainer = observer(({ returned = false }: { returned?: boolean }) => {
  const rootStore = useContext(RootStoreContext);
  const onboardingStore = rootStore.onboardingStore;
  const userStore = rootStore.userStore;
  const role = userStore.user?.role;
  const returnedUser = returned;
  const navigate = useNavigate();

  const [pTestStore] = useState(() => new PTestStore());

  // Load questions on component mount
  useEffect(() => {
    const collection = role === UserRole.Founder
      ? PTestCollection.PERSONALITY_E
      : PTestCollection.PERSONALITY_D;

    pTestStore.loadQuestions(collection);
  }, [role, pTestStore]);

  const handleNext = useCallback(async () => {
    if (pTestStore.canGoNext) {
      pTestStore.nextPage();
    } else if (pTestStore.isComplete) {
      onboardingStore.updatePTestResults(pTestStore.answers);

      try {
        let userId = onboardingStore.userStore.user?.uid;
        if (!userId && auth.currentUser) {
          userId = auth.currentUser.uid;
        }
        if (userId) {
          // Update onboardingStep to OnboardingCompleted after P-Test completion
          onboardingStore.updateOnboardingStep(OnboardingStep.OnboardingCompleted);

          const updatedWebOnboarding = {
            ...userStore.user?.webOnboarding,
            onboardingStep: OnboardingStep.OnboardingCompleted,
          };

          await updateUserProfileInDatabase(userId, {
            hasCompletedPTest: true,
            personality_results: onboardingStore.formData.personality_results,
            webOnboarding: updatedWebOnboarding,
          });
          onboardingStore.userStore.updateUserData({
            hasCompletedPTest: true,
            personality_results: onboardingStore.formData.personality_results,
            webOnboarding: updatedWebOnboarding,
          });
        }

        trackPersonalityTestCompleted();

        navigate('/dashboard');
      } catch (error) {
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pTestStore, onboardingStore, navigate]);

  const handlePrevious = useCallback(() => {
    pTestStore.prevPage();
  }, [pTestStore]);

  const handleUpdateAnswer = useCallback((questionId: string, answer: string) => {
    const answerNumber = parseInt(answer, 10);
    const question = pTestStore.questions.find(q => q.id === questionId);

    if (question) {
      // Only update the local P-Test store, don't save to onboarding store until completion
      pTestStore.updateAnswer(questionId, answerNumber, question.category, question.isFlipped);
    }
  }, [pTestStore]);

  const handleSignOut = useCallback(async () => {
    try {
      // Store user role for redirect after sign-out
      if (role) {
        sessionStorage.setItem('lastUserRole', role);
      }
      await signOut();
    } catch (error) {
    }
  }, [role]);

  return (
    <PTestStepsUi
      userRole={role || UserRole.SoftwareEngineer}
      returnedUser={returnedUser}
      progress={pTestStore.progress}
      currentPage={pTestStore.currentPage + 1}
      totalPages={pTestStore.totalPages}
      questions={pTestStore.currentQuestions}
      answers={pTestStore.answers}
      loading={pTestStore.loading}
      error={pTestStore.error}
      isContinuable={pTestStore.isContinuable}
      canGoPrev={pTestStore.canGoPrev}
      isLastPage={!pTestStore.canGoNext}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onUpdateAnswer={handleUpdateAnswer}
      onSignOut={handleSignOut}
    />
  );
});

export default PTestStepsContainer;
